// apps/web/src/admin/AdminApplicationsView.tsx
import { useEffect, useState } from 'react';
import { apiAdmin } from './apiAdmin.js';
import { DocPreviewModal } from './DocPreviewModal.js';
import { STAGE_LABEL, isGuideInputStage, type GuideStage } from '../registration/guideStages.js';

function formatExpertise(g: { expertise_areas?: string[]; expertise_other?: string | null } | null | undefined): string {
  const areas = (g?.expertise_areas ?? []).filter((a) => a !== 'other');
  const other = g?.expertise_other?.trim();
  const parts = [...areas, ...(other ? [`Other: ${other}`] : [])];
  return parts.join(', ');
}

export function AdminApplicationsView() {
  const [apps, setApps] = useState<any[] | null>(null);
  const [sel, setSel] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; kind: 'video' | 'resume' } | null>(null);

  async function reload() { const r = await apiAdmin.applications(); setApps(r.applications); }
  useEffect(() => { void reload(); }, []);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true); setErr(null);
    try { await fn(); await reload(); setSel(null); }
    catch (e: any) { setErr(e.message ?? String(e)); }
    finally { setBusy(false); }
  }

  function requestChanges(fn: (id: string, b: { reason: string }) => Promise<unknown>) {
    const reason = window.prompt('What should the guide change?')?.trim();
    if (!reason) return;
    void act(() => fn(sel.id, { reason }));
  }

  async function openDoc(kind: 'video' | 'resume') {
    setErr(null);
    try { const { url } = await apiAdmin.guideDocumentUrl(sel.id, kind); setPreview({ url, kind }); }
    catch (e: any) { setErr(e.message ?? String(e)); }
  }

  if (!apps) return <div className="text-ink-500">Loading…</div>;
  if (apps.length === 0) return <div className="text-ink-500">No pending applications.</div>;

  const stage = sel?.guide?.application_stage as GuideStage | undefined;

  return (
    <div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-ink-500"><th className="py-1">Name</th><th>Email</th><th>Submitted</th><th>Expertise</th><th>Stage</th></tr></thead>
        <tbody>
          {apps.map((a) => (
            <tr key={a.id} className="border-t border-ink-200 dark:border-white/10 cursor-pointer hover:bg-ink-50 dark:hover:bg-white/5" onClick={() => setSel(a)}>
              <td className="py-2">{a.displayName}{a.nameFlag ? <span className="ml-2 text-[0.7rem] rounded bg-red-100 text-red-700 px-1.5 py-0.5">flagged name</span> : null}</td><td>{a.email}</td>
              <td>{new Date(a.submittedAt).toLocaleDateString()}</td>
              <td>{a.role === 'learner' ? 'learner' : (formatExpertise(a.guide) || '—')}</td>
              <td>{a.guide?.application_stage
                ? <span className={`text-[0.72rem] rounded px-1.5 py-0.5 ${isGuideInputStage(a.guide.application_stage) ? 'bg-ink-100 text-ink-600 dark:bg-white/10 dark:text-ink-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'}`}>{STAGE_LABEL[a.guide.application_stage as GuideStage] ?? a.guide.application_stage}</span>
                : (a.role === 'learner' ? '—' : '')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {sel ? (
        <div className="fixed inset-0 z-40 flex">
          <div className="flex-1 bg-black/30" onClick={() => setSel(null)} />
          <aside className="w-[520px] bg-canvas-light dark:bg-canvas-dark border-l border-ink-200 dark:border-white/10 p-5 overflow-y-auto">
            <button onClick={() => setSel(null)} className="text-sm text-ink-500">Close</button>
            <h2 className="text-xl font-semibold mt-2">{sel.displayName}</h2>
            <p className="text-ink-500 text-sm">{sel.email} · {sel.fullName} · {sel.role}</p>
            {sel.nameFlag ? (
              <p className="mt-3 text-sm rounded bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 px-3 py-2">
                Flagged name words: <strong>{sel.nameFlag}</strong>
              </p>
            ) : null}
            {sel.guide ? (
              <>
                <dl className="mt-4 text-sm grid grid-cols-2 gap-y-1">
                  <dt className="text-ink-500">Age range</dt><dd>{sel.guide?.age_range}</dd>
                  <dt className="text-ink-500">Phone</dt><dd>{sel.guide?.phone}</dd>
                  <dt className="text-ink-500">City</dt><dd>{sel.guide?.city}</dd>
                  <dt className="text-ink-500">State</dt><dd>{sel.guide?.state}</dd>
                  <dt className="text-ink-500">Years of ASL</dt><dd>{sel.guide?.asl_years}</dd>
                  <dt className="text-ink-500">CDI</dt><dd>{sel.guide?.is_cdi ? 'Yes' : `No (interested: ${sel.guide?.interested_in_cdi})`}</dd>
                  <dt className="text-ink-500">Area of expertise</dt><dd>{formatExpertise(sel.guide) || '—'}</dd>
                  <dt className="text-ink-500">Availability</dt><dd>{(sel.guide?.availability_slots ?? []).join(', ')}</dd>
                  <dt className="text-ink-500">Background check</dt><dd>{sel.guide?.bg_check_consent ? 'agreed' : 'no'}</dd>
                  <dt className="text-ink-500">Code of conduct</dt><dd>{sel.guide?.code_of_conduct_consent ? 'agreed' : 'no'}</dd>
                </dl>
                <p className="mt-3 text-[0.78rem] text-ink-500">{sel.consent?.signed_text}</p>
              </>
            ) : null}
            {err ? <p className="text-red-600 text-sm mt-2">{err}</p> : null}
            <div className="mt-5 flex flex-wrap gap-2">
              {stage === 'video_review' && (<>
                <button disabled={busy} className="btn" onClick={() => void openDoc('video')}>Watch video</button>
                <button disabled={busy} className="btn" onClick={() => void openDoc('resume')}>View résumé</button>
                <button disabled={busy} className="btn" onClick={() => act(() => apiAdmin.approveVideo(sel.id))}>Approve → Rippling onboarding</button>
                <button disabled={busy} className="btn" onClick={() => requestChanges(apiAdmin.requestVideoChanges)}>Request changes</button>
              </>)}
              {stage === 'rippling_onboarding' && (<>
                <p className="text-[0.85rem] text-ink-500 w-full">
                  {sel.guide?.rippling_synced_at
                    ? `In Rippling since ${new Date(sel.guide.rippling_synced_at).toLocaleDateString()}. Waiting for payroll to finish the background check and forms.`
                    : 'Not yet sent to Rippling — the sync runs every 5 minutes. If this persists, check the backend logs for a token problem.'}
                </p>
                <button disabled={busy} className="btn" onClick={() => act(() => apiAdmin.ripplingFormsComplete(sel.id))}>Mark Rippling forms complete →</button>
              </>)}
              {stage === 'financial_review' && (<>
                <button disabled={busy} className="btn" onClick={() => act(() => apiAdmin.approveFinancial(sel.id))}>Approve → Authorize guide</button>
                <button disabled={busy} className="btn" onClick={() => requestChanges(apiAdmin.requestFinancialChanges)}>Request changes</button>
              </>)}
              {stage === 'video_upload' && (
                <p className="text-[0.85rem] text-ink-500">Waiting on the guide to upload their video and résumé.</p>
              )}
              {!stage && (
                <button disabled={busy} className="btn" onClick={() => act(() => apiAdmin.approveApplication(sel.id))}>Approve</button>
              )}
              {sel.guide && (
                <button disabled={busy}
                  className="btn bg-amber-500 hover:bg-amber-600 text-white border border-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500"
                  onClick={() => {
                    const reason = window.prompt('Reason for auto-approving (skips video, résumé, background check & financial review)?')?.trim();
                    if (!reason) return;
                    if (!window.confirm(`Auto-approve ${sel.email} and let them take calls now?`)) return;
                    void act(() => apiAdmin.autoApprove(sel.id, { reason }));
                  }}>Auto-Approve (skip all phases)</button>
              )}
              <button disabled={busy} className="btn" onClick={() => {
                const reason = window.prompt('Reason for rejecting? (this deletes the application account)')?.trim();
                if (!reason) return;
                if (!window.confirm(`Reject and DELETE ${sel.email}?`)) return;
                void act(() => apiAdmin.rejectApplication(sel.id, { reason }));
              }}>Reject (delete)</button>
            </div>
          </aside>
        </div>
      ) : null}

      {preview ? <DocPreviewModal url={preview.url} kind={preview.kind} onClose={() => setPreview(null)} /> : null}
    </div>
  );
}
